import { motion, AnimatePresence } from 'framer-motion'
import React from 'react'
import { useCartState, useCartDispatch } from '@/contexts/CartContext'
import { applyCoupon, removeCoupon } from 'CartActions'
import { toast } from 'react-hot-toast'
import CustomToast from '../CustomToast'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { track } from '@/lib/tracker'

const Loader = () => (
  <div className='w-full h-full flex justify-center items-center'>
    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
  </div>
)

const CouponForm = ({
  isCouponLoading,
  setIsCouponLoading,
}: {
  isCouponLoading: boolean
  setIsCouponLoading: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const [coupon, setCoupon] = React.useState('')
  const cart = useCartState()
  const cartDispatch = useCartDispatch()
  const {t: tCart} = useTranslation('cart');
  const {t: tAlerts} = useTranslation('alerts');
  const {locale} = useRouter();

  const sleep = (ms: number) => new Promise(res => setTimeout(res, ms))

  const SubmitCoupon = async () => {
  setIsCouponLoading(true)
  track({event_name: 'apply_coupon', metadata: {coupon: coupon}})

  try {
    if (coupon.trim() === '') {
      toast.custom(
        (t) => (
          <CustomToast
            t={t}
            message={tAlerts('coupon_empty')}
            type="error"
          />
        ),
        { duration: 500 }
      )
      return
    }

    await sleep(500)

    const response = await cartDispatch(applyCoupon(coupon))

    // ❌ Empty / invalid response
    if (!response || Object.keys(response).length === 0) {
      toast.custom(
        (t) => (
          <CustomToast
            t={t}
            message={tAlerts('coupon_invalid')}
            type="error"
          />
        ),
        { duration: 2000 }
      )
      return
    }

    // ✅ Success
    toast.custom(
      (t) => (
        <CustomToast
          t={t}
          message={tAlerts('coupon_applied')}
          type="success"
        />
      ),
      { duration: 2000 }
    )

  } catch (err) {
    toast.custom(
      (t) => (
        <CustomToast
          t={t}
          message={tAlerts('coupon_invalid')}
          type="error"
        />
      ),
      { duration: 2000 }
    )
  } finally {
    await sleep(1100)
    setIsCouponLoading(false)
  }
}

  const handleRemoveCoupon = async () => {
    setIsCouponLoading(true)
    try {
        await sleep(500);
        cartDispatch(removeCoupon())
        setCoupon('')
    } finally {
        await sleep(1000);
        setIsCouponLoading(false)
    }
  }

  React.useEffect(() => {
    if (cart.coupon) {
        setCoupon(cart.coupon);
    }
  }, [cart])
  return (
    <div>
      <form
        onSubmit={e => {
          e.preventDefault()
          cart.coupon ? handleRemoveCoupon() : SubmitCoupon()
        }}
        className={`flex items-center mt-2 py-1 px-2 md:p-2 border rounded-lg border-gray-200 ${locale === 'ar' ? 'flex-row' : 'flex-row-reverse'}`}
      >
        <input
          type="text"
          placeholder={`${tCart('couponPlaceholder')}`}
          className="flex-1 py-[5px] text-sm border border-white focus:outline-none focus:ring-0 focus:border-white focus:border-0"
          disabled={!!cart.coupon}
          value={coupon}
          onChange={e => setCoupon(e.target.value)}
          dir={`${locale === 'ar' ? 'rtl' : 'ltr'}`}
        />
        <AnimatePresence mode="wait">
          {cart.coupon ? (
            <motion.button
              type="submit"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2 }}
              className="bg-red text-white rounded-lg px-2 py-1 flex items-center justify-center w-12 h-8 text-xs md:text-sm md:w-16 md:h-8"
            >
              {isCouponLoading ? <Loader /> : `${tCart('remove')}`}
            </motion.button>
          ) : coupon.length > 0 && (
            <motion.button
              type="submit"
              key={isCouponLoading ? 'loading' : 'default'}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2 }}
              className="bg-emerald-400 text-white rounded-lg px-2 py-1 flex items-center justify-center w-12 h-8 text-xs md:text-sm md:w-16 md:h-8"
            >
              {isCouponLoading ? <Loader /> : `${tCart('add')}`}
            </motion.button>
          )}
        </AnimatePresence>
      </form>
    </div>
  )
}

export default CouponForm